import { z } from 'zod';
import { textPath } from '../typography/index';

export const id = z.string().regex(/^[a-zA-Z0-9_-]{1,80}$/);
const positive = z.number().finite().positive();
export const TemplateSchema = z.object({
  imageUrl: z.string().url().optional(), backgroundUrl: z.string().url().optional(),
  width: positive.max(20000), height: positive.max(20000),
  left: z.number().finite().min(0), top: z.number().finite().min(0),
  areaWidth: positive, areaHeight: positive,
  positioning: z.enum(['overlay', 'background']),
}).refine(t => t.left + t.areaWidth <= t.width + 0.01 && t.top + t.areaHeight <= t.height + 0.01, 'Invalid template rectangle');
export const PlacementSchema = z.object({
  id, label: z.string().min(1).max(100), technique: z.enum(['dtg', 'digital', 'sublimation', 'uv', 'dtfilm']),
  widthInches: positive.max(40), heightInches: positive.max(40),
  dpi: z.number().int().min(72).max(600), safeMargin: z.number().min(0).max(0.2),
  template: TemplateSchema, styleIds: z.array(z.number().int().positive()).max(200),
  conflicts: z.array(id).max(20).optional(),
}).refine(p => Math.round(p.widthInches * p.dpi) * Math.round(p.heightInches * p.dpi) <= 24000000, 'Print area exceeds renderer capacity');
const Base = z.object({
  id, x: z.number().finite().min(-1).max(2), y: z.number().finite().min(-1).max(2),
  width: positive.max(2), height: positive.max(2), rotation: z.number().finite().min(-360).max(360),
  opacity: z.number().min(0.1).max(1), visible: z.boolean(), locked: z.boolean(),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
});
export const TextElementSchema = Base.extend({
  type: z.literal('text'),
  text: z.string().max(120),
  font: z.string().min(1).max(100),
  curve: z.number().min(-150).max(150)
});
export const ElementSchema = z.discriminatedUnion('type', [
  TextElementSchema,
  Base.extend({ type: z.literal('image'), assetId: z.string().regex(/^[a-f0-9]{64}$/), recolor: z.boolean() }),
  Base.extend({ type: z.literal('shape'), shape: z.enum(['rect', 'ellipse']) }),
]);
export const TextSlotSchema = TextElementSchema.extend({ maxCharacters: z.number().int().min(1).max(120), minCharacters: z.number().int().min(1).max(120).optional() }).refine(slot=>(slot.minCharacters??1)<=slot.maxCharacters,'Minimum characters must not exceed maximum characters');
export const ProductSchema = z.object({
  id, name: z.string().min(1).max(200), providerProductId: z.number().int().positive().optional(),
  verified: z.boolean(), importedAt: z.string().optional(),
  variants: z.array(z.object({
    id, label: z.string().min(1).max(200), color: z.string().regex(/^#[0-9a-f]{6}$/i),
    placements: z.array(PlacementSchema).min(1).max(8),
  })).min(1).max(500),
  textSlots: z.record(id, z.array(TextSlotSchema).min(1).max(12)).optional(),
}).refine(p => new Set(p.variants.map(v => v.id)).size === p.variants.length, 'Duplicate variant')
  .refine(p => !p.textSlots || Object.keys(p.textSlots).every(key => p.variants.some(v => v.placements.some(place => place.id === key))), 'Text template placement is not available');
export const DesignSchema = z.object({
  version: z.literal(1), variantId: id,
  placements: z.record(id, z.array(ElementSchema).max(40)),
}).refine(d => Object.keys(d.placements).length <= 8, 'Too many placements');
export type Product = z.infer<typeof ProductSchema>;
export type Placement = z.infer<typeof PlacementSchema>;
export type Element = z.infer<typeof ElementSchema>;
export type TextSlot = z.infer<typeof TextSlotSchema>;
export type Design = z.infer<typeof DesignSchema>;
export type Asset = { id: string; width: number; height: number; vector: boolean; preview: string };
/** Characters supported by the bundled WordPress print fonts and native renderer. */
export function supportedPrintText(wording: string): boolean {
  return !!wording.trim() && /^[\p{Script=Latin}\p{Number}\p{Punctuation}\p{Zs}]+$/u.test(wording);
}
/** Conservative one-line limit from the merchant's fixed physical print box and selected font. */
export function suggestedTextCapacity(slot: TextSlot, placement: Placement): number {
  try {
    const sample = textPath('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', slot.font);
    const capHeight = textPath('Hgj', slot.font).height;
    const averageGlyphRatio = sample.width / 52 / capHeight;
    const physicalAspect = slot.width * placement.widthInches / (slot.height * placement.heightInches);
    return Math.max(1, Math.min(120, Math.floor(physicalAspect / averageGlyphRatio)));
  } catch {
    const physicalAspect = slot.width * placement.widthInches / (slot.height * placement.heightInches);
    return Math.max(1, Math.min(120, Math.floor(physicalAspect * 1.8)));
  }
}
export function textFitsSlot(slot: TextSlot, wording: string, placement: Placement): boolean {
  if (!wording.trim()) return false;
  try {
    const natural = textPath(wording, slot.font, slot.curve);
    const reference = textPath(slot.text, slot.font, slot.curve);
    const boxWidth = slot.width * placement.widthInches, boxHeight = slot.height * placement.heightInches;
    const fixedScale = Math.min(boxWidth / reference.width, boxHeight / reference.height);
    return natural.width * fixedScale <= boxWidth + 0.00001 && natural.height * fixedScale <= boxHeight + 0.00001;
  } catch {
    return wording.length <= (slot.maxCharacters || 120);
  }
}
export function personalText(design: Design): string {
  return Object.values(design.placements).flat().filter((e): e is Extract<Element, {type:'text'}> => e.type === 'text' && e.visible)
    .map(e => e.text.trim().replace(/\s+/gu, ' ')).filter(Boolean).join(' / ');
}
export function validateDesign(product: Product, input: unknown): Design {
  const d = DesignSchema.parse(input);
  const variant = product.variants.find(v => v.id === d.variantId);
  if (!variant) throw new Error('Select an available product variant.');
  for (const [key, elements] of Object.entries(d.placements)) {
    if (elements.length && !variant.placements.some(p => p.id === key)) throw new Error('This variant does not support all your designed placements.');
    if (new Set(elements.map(e => e.id)).size !== elements.length) throw new Error('Duplicate layer identifiers.');
    if (elements.some(e => e.type === 'text' && e.visible && !e.text.trim())) throw new Error('Enter text or remove the empty text layer.');
    const placement=variant.placements.find(p=>p.id===key);
    if(elements.some(e=>e.visible)&&placement?.conflicts?.some(id=>d.placements[id]?.some(e=>e.visible))) throw new Error('This product cannot print the selected placements together. Remove one of the conflicting designs.');
  }
  if (product.textSlots && Object.keys(product.textSlots).length) {
    const expectedKeys = Object.keys(product.textSlots).filter(key => variant.placements.some(p => p.id === key));
    if (!expectedKeys.length || Object.keys(d.placements).some(key => !expectedKeys.includes(key))) throw new Error('Only merchant-defined text placements may be personalised.');
    for (const key of expectedKeys) {
      const slots = product.textSlots[key], elements = d.placements[key] ?? [];
      const placement = variant.placements.find(p => p.id === key)!;
      if (slots.length !== elements.length) throw new Error('Every required text field must be completed.');
      for (const slot of slots) {
        const item = elements.find(e => e.id === slot.id);
        if (!item || item.type !== 'text') throw new Error('A required text field is missing.');
        for (const field of Object.keys(slot).filter(field => field !== 'text' && field !== 'maxCharacters' && field !== 'minCharacters')) {
          if ((item as any)[field] !== (slot as any)[field]) throw new Error('Text style and placement are set by the store and cannot be changed.');
        }
        if ([...item.text.trim()].length < (slot.minCharacters??1)) throw new Error(`Use at least ${slot.minCharacters??1} characters for this text area.`);
        if ([...item.text.trim()].length > slot.maxCharacters) throw new Error(`Text exceeds the ${slot.maxCharacters}-character limit for its print area.`);
        if (!supportedPrintText(item.text.trim())) throw new Error('Use Latin letters, numbers, punctuation or spaces. Emoji are not supported by these fonts.');
        if (item.text.trim()) {
          if (!textFitsSlot(slot, item.text, placement)) throw new Error('This wording is too wide for the store’s fixed text size. Shorten the text.');
        }
      }
    }
  }
  const text = personalText(d);
  if (!text || [...text].length > 120 || /[<>\p{Cc}\p{Cf}]/u.test(text)) throw new Error('Your visible custom text must contain 1–120 characters, without control characters or angle brackets.');
  return d;
}
